import "@/App.css";

import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { identifyJourneySession, installJourneyClicks, trackPageView } from "@/lib/journeyTracker";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { DesktopBackendGate } from "@/components/DesktopBackendGate";
import { isDesktopApp } from "@/services/config/env";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { MovieDirectorProvider } from "@/contexts/MovieDirectorContext";
import { LandingPageNew } from "@/pages/LandingPageNew/LandingPage";
import { FlowsPage } from "@/pages/LandingPageNew/FlowsPage";
import { AuthPage } from "@/pages/AuthPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { GoogleAuthCallbackPage } from "@/pages/GoogleAuthCallbackPage";
import { CreatorStudioLayout } from "@/components/dashboard/CreatorStudioLayout";
import { DashboardPage } from "@/pages/DashboardPage";
import { CharacterBuilderPage } from "@/pages/CharacterBuilderPage";
import { StorySetupPage } from "@/pages/StorySetupPage";
import { StoryGenerationPage } from "@/pages/StoryGenerationPage";
import { SceneEditorPage } from "@/pages/SceneEditorPage";
import { SceneGridEditor } from "@/pages/SceneGridEditor";
import { VideoEditorPage } from "@/pages/VideoEditorPage";
import { FriendsPage } from "@/pages/FriendsPage";
import { CollaborationPage } from "@/pages/CollaborationPage";
import { CollaborationReportPage } from "@/pages/CollaborationReportPage";
import { CollabChatPage } from "@/pages/CollabChatPage";
import { AdStudioPage } from "@/pages/AdStudioPage";
import { TaskHistoryPage } from "@/pages/TaskHistoryPage";
import { DoodleToStoryPage } from "@/pages/DoodleToStoryPage";
import { StoryFlowPage } from "@/pages/StoryFlowPage";
import { NarrationFlowPage } from "@/pages/NarrationFlowPage";
import { ScriptBasedFlowPage } from "@/pages/ScriptBasedFlowPage";
import { StoryBuilderFlowPage } from "@/pages/StoryBuilderFlowPage";
import { ReelsShortsFlowPage } from "@/pages/ReelsShortsFlowPage";
import { ScriptAudioAlignPage } from "@/pages/ScriptAudioAlignPage";
import { SongBasedFlowPage } from "@/pages/SongBasedFlowPage";
import { NewsFlowPage } from "@/pages/NewsFlowPage";
import { NarrationSceneListingPage } from "@/pages/NarrationSceneListingPage";
import { NarrationSceneGridEditor } from "@/pages/NarrationSceneGridEditor";
import { NodeWorkflowPage } from "@/pages/NodeWorkflowPage";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PublicOnlyRoute } from "@/components/PublicOnlyRoute";
import { AdminProtectedRoute } from "@/components/AdminProtectedRoute";
import { AgentOnlyCreateRoute } from "@/components/createPlan/AgentCreateRedirect";
import { AdminDashboardPage } from "@/pages/AdminDashboardPage";
import { AdminBillingRatesPage } from "@/pages/AdminBillingRatesPage";
import { PricingPageRoute } from "@/components/billing/PricingPageRoute";
import { BillingCheckoutPage } from "@/pages/BillingCheckoutPage";
import { InsufficientCreditsProvider } from "@/contexts/InsufficientCreditsContext";
import ParentDashboardPage from "@/pages/ParentDashboardPage";
import { TimelinePage } from "@/pages/TimelinePage";
import { SocialPublishPage } from "@/pages/SocialPublishPage";
import { AutoSeoPublishPage } from "@/pages/AutoSeoPublishPage";
import { ChannelAdvancedPage } from "@/pages/ChannelAdvancedPage";
import { ChannelAnalyticsPage } from "@/pages/ChannelAnalyticsPage";
import { ChannelTrendsPage } from "@/pages/ChannelTrendsPage";
import { ChannelVideosPage } from "@/pages/ChannelVideosPage";
import { ChannelNextIdeaPage } from "@/pages/ChannelNextIdeaPage";
import { ChannelVideoDetailPage } from "@/pages/ChannelVideoDetailPage";
import { ChannelKeywordsPage } from "@/pages/ChannelKeywordsPage";
import { ChannelCompetitorsPage } from "@/pages/ChannelCompetitorsPage";
import { DialogueSceneVideoBoardPage } from "@/pages/DialogueSceneVideoBoardPage";
import { LearningTutorialFlowPage } from "@/pages/LearningTutorialFlowPage";
import { LearningTutorialEditorPage } from "@/pages/LearningTutorialEditorPage";
import { TutorialFlowPage } from "@/pages/TutorialFlowPage";
import { TutorialFlow3Page } from "@/pages/TutorialFlow3Page";
import { TutorialFlow4Page } from "@/pages/TutorialFlow4Page";
import { ArtifactEditorRoute } from "@/artifactEditor/ArtifactEditorRoute";
import { AvatarCastChatPage } from "@/pages/AvatarCastChatPage";
import { CreativePartnershipPage } from "@/pages/CreativePartnershipPage";
import { PitchFlowPage } from "@/pages/PitchFlowPage";
import { PitchVideoEditorPage } from "@/pages/PitchVideoEditorPage";
import { MascotAnimationFlowPage } from "@/pages/MascotAnimationFlowPage";
import { AvatarCastPage } from "@/pages/AvatarCastPage";
import { DigitalTwinPage } from "@/pages/DigitalTwinPage";
import { CardCarouselDemoPage } from "@/pages/CardCarouselDemoPage";
import { NetworkDirectoryPage } from "@/pages/NetworkDirectoryPage";
import { NetworkProfileEditorPage } from "@/pages/NetworkProfileEditorPage";
import { NetworkPublicProfilePage } from "@/pages/NetworkPublicProfilePage";
import { NetworkInboxPage } from "@/pages/NetworkInboxPage";
import { NetworkThreadPage } from "@/pages/NetworkThreadPage";
import { NetworkLeaderboardPage } from "@/pages/NetworkLeaderboardPage";

const Router = isDesktopApp() ? HashRouter : BrowserRouter;

function JourneyTracker() {
  const location = useLocation();
  useEffect(() => {
    installJourneyClicks();
    identifyJourneySession();
  }, []);
  useEffect(() => {
    trackPageView(`${location.pathname}${location.search || ''}`);
  }, [location.pathname, location.search]);
  return null;
}

function AppRoutes() {
  return (
    <div className="min-h-screen theme-page-bg">
      <div className="noise-overlay" />
      <InsufficientCreditsProvider>
        <Routes>
          <Route path="/" element={<LandingPageNew />} />
          <Route path="/card-carousel-demo" element={<CardCarouselDemoPage />} />
          <Route path="/flows" element={<FlowsPage />} />
          <Route path="/login" element={<PublicOnlyRoute><AuthPage mode="login" /></PublicOnlyRoute>} />
          <Route path="/register" element={<PublicOnlyRoute><AuthPage mode="register" /></PublicOnlyRoute>} />
          <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/google/callback" element={<GoogleAuthCallbackPage />} />
          <Route element={<CreatorStudioLayout />}>
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/channel" element={<ProtectedRoute><ChannelAnalyticsPage /></ProtectedRoute>} />
            <Route path="/channel/advanced" element={<ProtectedRoute><ChannelAdvancedPage /></ProtectedRoute>} />
            <Route path="/channel/trends" element={<ProtectedRoute><ChannelTrendsPage /></ProtectedRoute>} />
            <Route path="/channel/videos" element={<ProtectedRoute><ChannelVideosPage /></ProtectedRoute>} />
            <Route path="/channel/next/:ideaId" element={<ProtectedRoute><ChannelNextIdeaPage /></ProtectedRoute>} />
            <Route path="/channel/videos/:videoId" element={<ProtectedRoute><ChannelVideoDetailPage /></ProtectedRoute>} />
            <Route path="/channel/keywords" element={<ProtectedRoute><ChannelKeywordsPage /></ProtectedRoute>} />
            <Route path="/channel/competitors" element={<ProtectedRoute><ChannelCompetitorsPage /></ProtectedRoute>} />
            <Route path="/tasks" element={<ProtectedRoute><TaskHistoryPage /></ProtectedRoute>} />
            <Route path="/partners/creative" element={<CreativePartnershipPage />} />
            <Route path="/social-publish" element={<ProtectedRoute><SocialPublishPage /></ProtectedRoute>} />
            <Route path="/social-publish/auto" element={<ProtectedRoute><AutoSeoPublishPage /></ProtectedRoute>} />
            <Route path="/admin" element={<AdminProtectedRoute><AdminDashboardPage /></AdminProtectedRoute>} />
            <Route path="/admin/billing" element={<AdminProtectedRoute><AdminBillingRatesPage /></AdminProtectedRoute>} />
            <Route path="/pricing" element={<PricingPageRoute />} />
            <Route path="/tutorial/create" element={<Navigate to="/script-audio-align" state={{ prefill: { mode: 'topic' } }} replace />} />
            <Route path="/narration/:projectId/edit" element={<ProtectedRoute><NarrationSceneGridEditor /></ProtectedRoute>} />
            <Route path="/narration/:projectId/video-editor" element={<ProtectedRoute><VideoEditorPage /></ProtectedRoute>} />
            <Route path="/narration/:projectId/workflow" element={<ProtectedRoute><NodeWorkflowPage /></ProtectedRoute>} />
            <Route path="/stories/:storyId/edit" element={<ProtectedRoute><SceneGridEditor /></ProtectedRoute>} />
            <Route path="/stories/:storyId/video-editor" element={<ProtectedRoute><VideoEditorPage /></ProtectedRoute>} />
            <Route path="/stories/:storyId/workflow" element={<ProtectedRoute><NodeWorkflowPage /></ProtectedRoute>} />
            {/* Influencer Network — inside CreatorStudioLayout so sidebar + top chrome render */}
            <Route path="/network" element={<ProtectedRoute><NetworkDirectoryPage /></ProtectedRoute>} />
            <Route path="/network/me" element={<ProtectedRoute><NetworkProfileEditorPage /></ProtectedRoute>} />
            <Route path="/network/leaderboard" element={<ProtectedRoute><NetworkLeaderboardPage /></ProtectedRoute>} />
            <Route path="/network/inbox" element={<ProtectedRoute><NetworkInboxPage /></ProtectedRoute>} />
            <Route path="/network/inbox/:conversationId" element={<ProtectedRoute><NetworkThreadPage /></ProtectedRoute>} />
            <Route path="/network/:handle" element={<ProtectedRoute><NetworkPublicProfilePage /></ProtectedRoute>} />
          </Route>
          <Route path="/characters" element={<ProtectedRoute><CharacterBuilderPage /></ProtectedRoute>} />
          <Route path="/avatar-cast" element={<ProtectedRoute><AvatarCastChatPage /></ProtectedRoute>} />
          <Route path="/avatar-cast/studio" element={<ProtectedRoute><AvatarCastPage /></ProtectedRoute>} />
          <Route path="/digital-twin" element={<ProtectedRoute><DigitalTwinPage /></ProtectedRoute>} />
          <Route path="/avatar/callback" element={<ProtectedRoute><DigitalTwinPage /></ProtectedRoute>} />
          <Route path="/avatar/success" element={<ProtectedRoute><DigitalTwinPage /></ProtectedRoute>} />
          <Route path="/doodle-to-story" element={<ProtectedRoute><DoodleToStoryPage /></ProtectedRoute>} />
          <Route path="/stories/flow" element={<ProtectedRoute><AgentOnlyCreateRoute flowId="" /></ProtectedRoute>} />
          <Route path="/narration/create" element={<ProtectedRoute><AgentOnlyCreateRoute flowId="narration"><NarrationFlowPage /></AgentOnlyCreateRoute></ProtectedRoute>} />
          <Route path="/script-based/create" element={<ProtectedRoute><AgentOnlyCreateRoute flowId="script_based"><ScriptBasedFlowPage /></AgentOnlyCreateRoute></ProtectedRoute>} />
          <Route path="/storybuilder/create" element={<ProtectedRoute><AgentOnlyCreateRoute flowId="storybuilder"><StoryBuilderFlowPage /></AgentOnlyCreateRoute></ProtectedRoute>} />
          <Route path="/reels-shorts/create" element={<ProtectedRoute><AgentOnlyCreateRoute flowId="reels_shorts"><ReelsShortsFlowPage /></AgentOnlyCreateRoute></ProtectedRoute>} />
          <Route path="/reels-shorts/:projectId/video-editor" element={<ProtectedRoute><VideoEditorPage /></ProtectedRoute>} />
          <Route path="/script-audio-align" element={<ProtectedRoute><AgentOnlyCreateRoute flowId="script_audio_align"><ScriptAudioAlignPage /></AgentOnlyCreateRoute></ProtectedRoute>} />
          <Route path="/song-based/create" element={<ProtectedRoute><AgentOnlyCreateRoute flowId="song_based"><SongBasedFlowPage /></AgentOnlyCreateRoute></ProtectedRoute>} />
          <Route path="/narration/:projectId/scenes" element={<ProtectedRoute><NarrationSceneListingPage /></ProtectedRoute>} />
          <Route path="/news/create" element={<ProtectedRoute><AgentOnlyCreateRoute flowId="news_video"><NewsFlowPage /></AgentOnlyCreateRoute></ProtectedRoute>} />
          <Route path="/news/:projectId/video-editor" element={<ProtectedRoute><VideoEditorPage /></ProtectedRoute>} />
          <Route path="/tutorial/flow3" element={<ProtectedRoute><AgentOnlyCreateRoute flowId="script_audio_align" /></ProtectedRoute>} />
          <Route path="/tutorial/flow3/:projectId" element={<ProtectedRoute><TutorialFlow3Page /></ProtectedRoute>} />
          <Route path="/tutorial/artifacts/:artifactId/studio" element={<ProtectedRoute><ArtifactEditorRoute /></ProtectedRoute>} />
          <Route path="/tutorial-v3/create" element={<ProtectedRoute><AgentOnlyCreateRoute flowId="script_audio_align" /></ProtectedRoute>} />
          <Route path="/tutorial/flow4" element={<ProtectedRoute><AgentOnlyCreateRoute flowId="script_audio_align" /></ProtectedRoute>} />
          <Route path="/pitch/create" element={<ProtectedRoute><AgentOnlyCreateRoute flowId="pitch_recording"><PitchFlowPage /></AgentOnlyCreateRoute></ProtectedRoute>} />
          <Route path="/pitch/:projectId/record" element={<ProtectedRoute><PitchFlowPage /></ProtectedRoute>} />
          <Route path="/pitch/:projectId/video-editor" element={<ProtectedRoute><PitchVideoEditorPage /></ProtectedRoute>} />
          <Route path="/mascot/create" element={<ProtectedRoute><AgentOnlyCreateRoute flowId="mascot_animation"><MascotAnimationFlowPage /></AgentOnlyCreateRoute></ProtectedRoute>} />
          <Route path="/mascot/:projectId/:step?" element={<ProtectedRoute><MascotAnimationFlowPage /></ProtectedRoute>} />
          <Route path="/learning/create" element={<ProtectedRoute><AgentOnlyCreateRoute flowId="script_audio_align" /></ProtectedRoute>} />
          <Route path="/learning/:projectId/editor" element={<ProtectedRoute><LearningTutorialEditorPage /></ProtectedRoute>} />
          <Route path="/dialogue/create" element={<ProtectedRoute><AgentOnlyCreateRoute flowId="dialogue"><StorySetupPage /></AgentOnlyCreateRoute></ProtectedRoute>} />
          <Route path="/stories/new" element={<ProtectedRoute><AgentOnlyCreateRoute flowId="dialogue"><StorySetupPage /></AgentOnlyCreateRoute></ProtectedRoute>} />
          <Route path="/stories/:storyId/generate" element={<ProtectedRoute><StoryGenerationPage /></ProtectedRoute>} />
          <Route path="/stories/:storyId/dialogue-scene-board" element={<ProtectedRoute><DialogueSceneVideoBoardPage /></ProtectedRoute>} />
          <Route path="/stories/:storyId/edit-old" element={<ProtectedRoute><SceneEditorPage /></ProtectedRoute>} />
          <Route path="/stories/:storyId/ads" element={<ProtectedRoute><AdStudioPage /></ProtectedRoute>} />
          <Route path="/credits" element={<ProtectedRoute><Navigate to="/pricing" replace /></ProtectedRoute>} />
          <Route path="/billing/checkout" element={<BillingCheckoutPage />} />
          <Route path="/parent-dashboard" element={<ProtectedRoute><ParentDashboardPage /></ProtectedRoute>} />
          <Route path="/friends" element={<ProtectedRoute><FriendsPage /></ProtectedRoute>} />
          <Route path="/collab/new" element={<ProtectedRoute><CollabChatPage /></ProtectedRoute>} />
          <Route path="/collab/chat/:sessionId" element={<ProtectedRoute><CollabChatPage /></ProtectedRoute>} />
          <Route path="/collab/:sessionId" element={<ProtectedRoute><CollaborationPage /></ProtectedRoute>} />
          <Route path="/collab/report/:sessionId" element={<ProtectedRoute><CollaborationReportPage /></ProtectedRoute>} />
          <Route path="/timeline" element={<ProtectedRoute><TimelinePage /></ProtectedRoute>} />
        </Routes>
      </InsufficientCreditsProvider>
    </div>
  );
}

function App() {
  return (
    <AppErrorBoundary>
      <DesktopBackendGate>
        <ThemeProvider>
          <MovieDirectorProvider>
            <Router>
              <JourneyTracker />
              <AppRoutes />
            </Router>
            <Toaster position="top-right" richColors />
          </MovieDirectorProvider>
        </ThemeProvider>
      </DesktopBackendGate>
    </AppErrorBoundary>
  );
}

export default App;
